<template>
  <!--
    Borna bug batch 1 (items 4 + 8). The canonical V2 logout dialog, rendered
    ONCE at the shell root (App/index.vue) and raised by every logout entry
    point through `app/logoutConfirm`. Structure and copy are the reference's
    own (`borna-student-journey-v2.html`: `.logout-dialog-backdrop` >
    `.logout-dialog` > icon / h2 + p / two actions), not approximated.

    It owns no auth behaviour: confirming emits `confirm`, and the shell calls
    the one existing `AuthService.logout`.
  -->
  <div
    class="LogoutConfirmDialog-backdrop"
    data-testid="logout-confirm-dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby="logout-dialog-title"
    @click.self="$emit('cancel')"
  >
    <!-- `tabindex="-1"` only so the dialog can take focus programmatically on
         open; it never enters the tab order. Escape is handled once, on
         `document`, rather than also here -- a bubbling keydown would
         otherwise emit `cancel` twice. -->
    <section class="LogoutConfirmDialog" ref="dialog" tabindex="-1">
      <span class="LogoutConfirmDialog-icon" aria-hidden="true">
        <student-home-icon name="logout" :size="25" />
      </span>
      <div>
        <h2 id="logout-dialog-title">{{ $t('logoutConfirm.title') }}</h2>
        <p>{{ $t('logoutConfirm.body') }}</p>
      </div>
      <div class="LogoutConfirmDialog-actions">
        <button
          type="button"
          class="LogoutConfirmDialog-confirm"
          data-testid="logout-confirm"
          ref="confirm"
          @click="$emit('confirm')"
        >
          {{ $t('logoutConfirm.confirm') }}
        </button>
        <button
          type="button"
          class="LogoutConfirmDialog-cancel"
          data-testid="logout-cancel"
          @click="$emit('cancel')"
        >
          {{ $t('logoutConfirm.cancel') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

export default {
  name: 'LogoutConfirmDialog',
  components: { StudentHomeIcon },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    // Cancel is the safe default, so focus lands on the dialog itself rather
    // than pre-arming the destructive action.
    if (this.$refs.dialog) this.$refs.dialog.focus()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
// Measured from the V2 reference's own `.logout-dialog*` rules. The red is
// the shared `$borna-danger` the sidebar logout row already uses.
$lc-danger: $borna-danger;
$lc-danger-50: #fef2f2;

.LogoutConfirmDialog-backdrop {
  position: fixed;
  inset: 0;
  // Above the student bottom nav (40) and the sidebar drawer; this element is
  // a child of the shell root, so nothing traps it in a lower stacking context.
  z-index: 160;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(7, 20, 71, 0.38);
  backdrop-filter: blur(8px);
}

.LogoutConfirmDialog {
  outline: none;
  display: grid;
  justify-items: center;
  gap: 16px;
  width: min(100%, 420px);
  padding: 26px;
  text-align: center;
  background: #fff;
  border: 1px solid rgba(220, 38, 38, 0.12);
  border-radius: 24px;
  box-shadow: 0 24px 70px rgba(7, 20, 71, 0.22);
}

.LogoutConfirmDialog-icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 17px;
  color: $lc-danger;
  background: $lc-danger-50;
}

.LogoutConfirmDialog h2 {
  margin: 0 0 7px;
  font-size: 18px;
  color: $borna-ink;
}

.LogoutConfirmDialog p {
  margin: 0;
  font-size: 12px;
  line-height: 1.9;
  color: $borna-muted;
}

.LogoutConfirmDialog-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
  width: 100%;
}

.LogoutConfirmDialog-confirm,
.LogoutConfirmDialog-cancel {
  min-height: 44px;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  border-radius: 13px;
  cursor: pointer;
}

.LogoutConfirmDialog-confirm {
  color: #fff;
  background: $lc-danger;
  border: 0;

  &:hover,
  &:focus-visible {
    background: darken($lc-danger, 6%);
  }
}

.LogoutConfirmDialog-cancel {
  color: $borna-ink;
  background: #fff;
  border: 1px solid $borna-border;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
  }
}

// V2 stacks the two actions on its narrowest tier so neither label wraps.
@include borna-narrow {
  .LogoutConfirmDialog-actions {
    grid-template-columns: 1fr;
  }
}
</style>
